import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import DashboardShell, { StatCard } from "./DashboardShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { BedDouble, ExternalLink, Calculator, Wallet } from "lucide-react";

const defaults = { transport: 0, restauration: 0, hebergement_nuit: 0, marketing: 0, divers: 0 };

export default function BudgetPlanner() {
  const [bookings, setBookings] = useState([]);
  const [eventId, setEventId] = useState("");
  const [nights, setNights] = useState(2);
  const [costs, setCosts] = useState(defaults);

  useEffect(() => {
    api.get("/my/bookings").then((r) => {
      const paid = r.data.filter((b) => b.payment_status === "paid" && b.item_type === "booth" && b.event);
      setBookings(paid);
      if (paid.length && !eventId) setEventId(paid[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const selected = useMemo(() => bookings.find((b) => b.id === eventId), [bookings, eventId]);
  const stand = selected?.amount || 0;
  const hebergement = (costs.hebergement_nuit || 0) * nights;
  const total = stand + (costs.transport || 0) + (costs.restauration || 0) + hebergement + (costs.marketing || 0) + (costs.divers || 0);

  const bookingUrl = selected?.event?.city
    ? `https://www.booking.com/searchresults.fr.html?ss=${encodeURIComponent(selected.event.city)}&checkin=${(selected.event.start_date || "").slice(0,10)}&checkout=${(selected.event.end_date || "").slice(0,10)}&group_adults=1&no_rooms=1${process.env.REACT_APP_BOOKING_AFFILIATE_ID ? `&aid=${process.env.REACT_APP_BOOKING_AFFILIATE_ID}` : ""}`
    : null;

  return (
    <DashboardShell title="Budget prévisionnel" subtitle="Calculez le coût total de votre prochain événement">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Stands réservés" value={bookings.length} />
        <StatCard label="Total stands" value={`${bookings.reduce((s, b) => s + (b.amount || 0), 0).toFixed(0)}€`} />
        <StatCard label="Budget en cours" value={`${total.toFixed(0)}€`} accent />
        <StatCard label="Nuitées prévues" value={nights} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2 bg-white border border-ink/10 rounded-md p-6 lg:p-8 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="h-4 w-4 text-accent" />
            <h2 className="font-heading font-medium text-lg text-ink">Saisir mes coûts</h2>
          </div>

          {bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucun stand réservé pour l'instant. Réservez d'abord un stand pour activer le calculateur.
            </p>
          ) : (
            <div>
              <Label>Événement</Label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="budget-event"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {bookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.event?.title} — {b.event?.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <Label>Transport (€)</Label>
              <Input
                type="number"
                data-testid="budget-transport"
                value={costs.transport}
                onChange={(e) => setCosts({ ...costs, transport: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Restauration (€)</Label>
              <Input
                type="number"
                data-testid="budget-restauration"
                value={costs.restauration}
                onChange={(e) => setCosts({ ...costs, restauration: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Hébergement (€ / nuit)</Label>
              <Input
                type="number"
                data-testid="budget-hebergement-nuit"
                value={costs.hebergement_nuit}
                onChange={(e) => setCosts({ ...costs, hebergement_nuit: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Nombre de nuits</Label>
              <Input
                type="number"
                min={0}
                data-testid="budget-nights"
                value={nights}
                onChange={(e) => setNights(parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Marketing / supports (€)</Label>
              <Input
                type="number"
                data-testid="budget-marketing"
                value={costs.marketing}
                onChange={(e) => setCosts({ ...costs, marketing: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Divers (€)</Label>
              <Input
                type="number"
                data-testid="budget-divers"
                value={costs.divers}
                onChange={(e) => setCosts({ ...costs, divers: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
        </div>

        {/* Recap + hotel */}
        <div className="space-y-4">
          <div className="bg-white border border-ink/10 rounded-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="h-4 w-4 text-accent" />
              <p className="text-label text-muted-foreground">Récapitulatif</p>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-ink/10">
                <tr><td className="py-2 text-ink/60">Stand</td><td className="py-2 text-right font-medium">{stand.toFixed(0)}€</td></tr>
                <tr><td className="py-2 text-ink/60">Transport</td><td className="py-2 text-right font-medium">{(costs.transport || 0).toFixed(0)}€</td></tr>
                <tr><td className="py-2 text-ink/60">Restauration</td><td className="py-2 text-right font-medium">{(costs.restauration || 0).toFixed(0)}€</td></tr>
                <tr><td className="py-2 text-ink/60">Hébergement × {nights}</td><td className="py-2 text-right font-medium">{hebergement.toFixed(0)}€</td></tr>
                <tr><td className="py-2 text-ink/60">Marketing</td><td className="py-2 text-right font-medium">{(costs.marketing || 0).toFixed(0)}€</td></tr>
                <tr><td className="py-2 text-ink/60">Divers</td><td className="py-2 text-right font-medium">{(costs.divers || 0).toFixed(0)}€</td></tr>
              </tbody>
            </table>
            <div className="border-t-2 border-ink mt-3 pt-3 flex justify-between items-baseline">
              <span className="font-heading font-medium text-ink">Total estimé</span>
              <span data-testid="budget-total" className="font-heading font-black text-2xl text-ink">{total.toFixed(0)}€</span>
            </div>
          </div>

          {/* Hotel pre-booking */}
          <div className="bg-ink text-cream rounded-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <BedDouble className="h-4 w-4 text-accent" />
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-cream/60">Pré-réserver</p>
            </div>
            <h3 className="font-heading font-medium text-lg mb-2">
              Hébergement <em className="font-display text-accent">à proximité</em>
            </h3>
            {selected?.event ? (
              <>
                <p className="text-xs text-cream/65 mb-4 leading-relaxed">
                  {selected.event.city} — du {new Date(selected.event.start_date).toLocaleDateString("fr-FR")} au {new Date(selected.event.end_date).toLocaleDateString("fr-FR")}
                </p>
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hotel-booking-link"
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider bg-accent text-ink hover:bg-cream transition"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Chercher sur Booking
                </a>
              </>
            ) : (
              <p className="text-xs text-cream/65">Sélectionnez un événement pour voir les hébergements disponibles.</p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed px-1">
            Estimations indicatives. Ce calculateur n'enregistre pas vos données ; il vous aide simplement à anticiper votre budget de saison.
          </p>
        </div>
      </div>
    </DashboardShell>
  );
}
